import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  entityTypeConfig,
  badgeConfig,
  verificationStats,
  verifiedEntities,
  verificationBenefits,
  verificationRules,
  getVerifiedByType,
  getVerifiedByBadge,
  type VerificationEntityType,
  type VerificationBadgeLevel,
  type VerifiedEntity,
} from '@/mocks/rfwVerification';

function EntityBadge({ type }: { type: VerificationEntityType }) {
  const c = entityTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function LevelBadge({ level }: { level: VerificationBadgeLevel }) {
  const c = badgeConfig[level];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function VerificacionSection() {
  const [activeTab, setActiveTab] = useState<'entidades' | 'insignias' | 'beneficios'>('entidades');
  const [typeFilter, setTypeFilter] = useState<VerificationEntityType | 'todas'>('todas');
  const [levelFilter, setLevelFilter] = useState<VerificationBadgeLevel | 'todas'>('todas');

  const filteredByType = getVerifiedByType(typeFilter);
  const filtered = getVerifiedByBadge(levelFilter).filter((e) =>
    typeFilter === 'todas' ? true : e.entityType === typeFilter
  );

  const tabs = [
    { id: 'entidades' as const, label: 'Verificados', icon: 'ri-verified-badge-line', badge: verifiedEntities.length },
    { id: 'insignias' as const, label: 'Insignias', icon: 'ri-shield-star-line', badge: 3 },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-star-line', badge: 3 },
  ];

  const typeFilters: { id: VerificationEntityType | 'todas'; icon: string; label: string }[] = [
    { id: 'todas', icon: 'ri-list-check-3', label: 'Todos' },
    ...Object.entries(entityTypeConfig).map(([key, c]) => ({ id: key as VerificationEntityType, icon: c.icon, label: c.labelPlural })),
  ];

  const levelFilters: { id: VerificationBadgeLevel | 'todas'; icon: string; label: string }[] = [
    { id: 'todas', icon: 'ri-list-check-3', label: 'Todos los Niveles' },
    ...Object.entries(badgeConfig).map(([key, c]) => ({ id: key as VerificationBadgeLevel, icon: c.icon, label: c.label })),
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            SISTEMA DE VERIFICACIÓN
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            El Sello de <span className="text-emerald-600">Confianza</span> RFW
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Cinco tipos de entidad, tres niveles de insignia y un solo estándar: la verificación RFW solo la otorgan los Super Admins. Cada insignia es una promesa de autenticidad, calidad y confianza para toda la comunidad.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Verificados', value: (verificationStats.totalVerificados / 1000).toFixed(1) + 'K', icon: 'ri-verified-badge-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Premium', value: verificationStats.premiumCount.toString(), icon: 'ri-vip-crown-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Oficiales', value: verificationStats.oficialCount.toString(), icon: 'ri-shield-check-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Tasa Aprobación', value: verificationStats.tasaAprobacion, icon: 'ri-check-double-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Tiempo Medio', value: verificationStats.tiempoMedioVerificacion, icon: 'ri-timer-line', color: 'text-slate-600 bg-slate-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'entidades' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTypeFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    typeFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {levelFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setLevelFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    levelFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.slice(0, 9).map((entity) => (
                <VerificationCard key={entity.id} entity={entity} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'insignias' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(badgeConfig).map(([key, c]) => {
                const count = verifiedEntities.filter((e) => e.badgeLevel === key).length;
                return (
                  <div key={key} className={`p-5 rounded-2xl border-2 ${c.borderColor} bg-background-100 text-center relative overflow-hidden`}>
                    <div className={`absolute top-0 left-0 right-0 h-1 ${c.bgColor}`}></div>
                    <div className={`w-16 h-16 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-3 mt-3`}>
                      <i className={`${c.iconBadge} text-3xl ${c.color}`}></i>
                    </div>
                    <p className="text-lg font-extrabold text-foreground-950 mb-1">{c.label}</p>
                    <p className="text-[11px] text-foreground-500 mb-2">{c.description}</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${c.color}`}>
                      <i className="ri-group-line text-[10px]"></i>
                      {count} entidades
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Verificada', level: 'verificada' as const, desc: 'Identidad confirmada. El primer paso: demostrar que sos quien decís ser.' },
                { label: 'Oficial', level: 'oficial' as const, desc: 'Trayectoria comprobada. Calidad consistente y comunidad fiel.' },
                { label: 'Premium', level: 'premium' as const, desc: 'Excelencia. Lo mejor de lo mejor en el ecosistema RFW.' },
              ].map((item) => {
                const entities = verifiedEntities.filter((e) => e.badgeLevel === item.level).slice(0, 4);
                return (
                  <div key={item.level} className={`p-4 rounded-xl bg-background-100 border ${badgeConfig[item.level].borderColor}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-full ${badgeConfig[item.level].bgColor} flex items-center justify-center`}>
                        <i className={`${badgeConfig[item.level].iconBadge} text-sm ${badgeConfig[item.level].color}`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{item.label}</p>
                        <p className="text-[10px] text-foreground-500">{item.desc}</p>
                      </div>
                    </div>
                    <div className="flex -space-x-2">
                      {entities.map((e) => (
                        <img key={e.id} src={e.entityPhotoUrl} alt={e.entityName} className="w-8 h-8 rounded-full object-cover border-2 border-background-100" title={e.entityName} />
                      ))}
                      {entities.length > 0 && (
                        <span className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center text-[9px] font-bold text-foreground-500 border-2 border-background-100">
                          +{entities.length}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'beneficios' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {verificationBenefits.map((benefit) => (
                <div key={benefit.id} className={`p-5 rounded-2xl ${benefit.bgColor} border border-background-200/50`}>
                  <div className={`w-12 h-12 rounded-full bg-background-50 flex items-center justify-center mb-3`}>
                    <i className={`${benefit.icon} text-xl ${benefit.color}`}></i>
                  </div>
                  <p className="text-base font-bold text-foreground-950 mb-1.5">{benefit.title}</p>
                  <p className="text-[11px] text-foreground-500 mb-3 leading-relaxed">{benefit.description}</p>
                  <ul className="space-y-1.5">
                    {benefit.bulletPoints.map((bp, i) => (
                      <li key={i} className="text-[10px] text-foreground-600 flex items-start gap-1.5">
                        <i className={`${benefit.color} text-[9px] ri-check-line mt-0.5 shrink-0`}></i>
                        {bp}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {verificationRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2">
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/verificacion"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-verified-badge-line"></i>
            Ver Todas las Entidades Verificadas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-violet-100/70 border border-violet-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-admin-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Solo SA puede verificar.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            En RFW la verificación no es automática ni está a la venta. Solo los Super Admins tienen la autoridad de otorgar, modificar o revocar una insignia de verificación. Cada verificación es una decisión informada basada en mérito, trayectoria y calidad. Porque la confianza no se compra — se gana y se verifica.
          </p>
        </div>
      </div>
    </section>
  );
}

function VerificationCard({ entity }: { entity: VerifiedEntity }) {
  const tc = entityTypeConfig[entity.entityType];
  const bc = badgeConfig[entity.badgeLevel];

  return (
    <Link
      to="/verificacion"
      className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-200 group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="relative shrink-0">
          <img src={entity.entityPhotoUrl} alt={entity.entityName} className="w-12 h-12 rounded-xl object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${bc.bgColor} flex items-center justify-center border-2 border-background-100`}>
            <i className={`${bc.iconBadge} text-[9px] ${bc.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-foreground-950 line-clamp-1">{entity.entityName}</p>
          <p className="text-[10px] text-foreground-500">{entity.category}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <EntityBadge type={entity.entityType} />
            <LevelBadge level={entity.badgeLevel} />
          </div>
        </div>
      </div>
      <p className="text-[10px] text-foreground-600 line-clamp-2 mb-2">{entity.description}</p>
      <div className="flex items-center justify-between text-[9px] text-foreground-400">
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line text-[8px]"></i>
          {entity.verifiedAtRelative}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-group-line text-[8px]"></i>
          {(entity.stats.followers / 1000).toFixed(0)}K
        </span>
      </div>
    </Link>
  );
}